import { Fragment, useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  BookOpenText,
  CalendarClock,
  CalendarDays,
  ChevronDown,
  ClipboardList,
  FileSignature,
  FileText,
  Inbox,
  LayoutDashboard,
  ListChecks,
  Menu,
  PanelLeftClose,
  PanelLeftOpen,
  ScrollText,
  SlidersHorizontal,
  UsersRound,
  X,
  type LucideIcon,
} from "lucide-react";
import { ArrowInline } from "@/lib/Icon";

import { useBootstrap } from "@/lib/api/hooks";
import {
  useAvailabilityEditorEnabled,
  useClinicianSession,
} from "@/lib/api/clinicianHooks";

/**
 * Clinician shell — a sibling of AdminLayout, not a variant of it.
 *
 * Deliberately its own surface: the admin portal is organised around ops
 * and analytics, this one around a clinician's working day (queue first,
 * patient context alongside it). Sharing a shell would mean sharing a nav,
 * and a clinician has no business in billing or lifecycle campaigns —
 * `require_clinical` refuses those APIs anyway.
 *
 * The visual language matches AdminLayout on purpose: same sidebar
 * geometry, same nav-link states. Someone with both roles shouldn't have
 * to relearn the furniture.
 */

const NAV_GROUPS: {
  label: string;
  items: { to: string; title: string; icon: LucideIcon }[];
}[] = [
  {
    label: "Overview",
    items: [
      {
        to: "/clinician-portal/overview",
        title: "Overview",
        icon: LayoutDashboard,
      },
    ],
  },
  {
    label: "Work",
    items: [
      { to: "/clinician-portal", title: "Inbox", icon: Inbox },
      { to: "/clinician-portal/patients", title: "Patients", icon: UsersRound },
      {
        to: "/clinician-portal/today",
        title: "Clinical worklist",
        icon: ListChecks,
      },
      {
        to: "/clinician-portal/reports",
        title: "Reporting",
        icon: ScrollText,
      },
    ],
  },
];

export function ClinicianLayout() {
  const { data } = useBootstrap();
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(
    () => window.localStorage.getItem("clinician-sidebar-collapsed") === "true",
  );

  useEffect(() => {
    window.localStorage.setItem(
      "clinician-sidebar-collapsed",
      String(collapsed),
    );
  }, [collapsed]);

  // RequireClinical is the gate; this only avoids drawing a half-built
  // shell on the first render of a fresh session, before bootstrap lands.
  if (!data?.is_admin) return null;

  // Full admins get a way back; a clinical-only account has nowhere to go.
  const backToAdmin = !["clinical", "clinical_supervisor"].includes(
    data.admin_role ?? "",
  );

  return (
    <div className="min-h-screen bg-page text-ink">
      {/* Mobile top bar (hamburger) */}
      <header className="md:hidden sticky top-0 z-50 border-b border-line bg-raised/80 backdrop-blur">
        <div className="px-15 py-12 flex items-center justify-between">
          <Brand />
          <button
            type="button"
            aria-label={drawerOpen ? "Close navigation" : "Open navigation"}
            onClick={() => setDrawerOpen((v) => !v)}
            className="p-6 rounded border border-line hover:bg-raised"
          >
            {drawerOpen ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </header>

      {/* Mobile drawer */}
      {drawerOpen && (
        <div className="md:hidden fixed inset-0 z-40">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setDrawerOpen(false)}
          />
          <aside className="absolute inset-y-0 left-0 w-[260px] bg-page border-r border-line pt-[64px] flex flex-col">
            <SidebarNav onNavigate={() => setDrawerOpen(false)} />
            <SidebarFooter
              email={data.patient.email}
              backToAdmin={backToAdmin}
              onBackToAdmin={() => navigate("/admin")}
            />
          </aside>
        </div>
      )}

      {/* Desktop sidebar */}
      <aside
        className={`hidden md:flex fixed inset-y-0 left-0 z-40 flex-col border-r border-line bg-raised/40 transition-[width] duration-200 ${collapsed ? "w-[64px]" : "w-[230px]"}`}
      >
        <div
          className={`py-16 border-b border-line flex items-center ${collapsed ? "px-10 justify-center" : "px-20 justify-between gap-8"}`}
        >
          {!collapsed && <Brand />}
          <button
            type="button"
            onClick={() => setCollapsed((value) => !value)}
            aria-label={collapsed ? "Expand navigation" : "Minimize navigation"}
            title={collapsed ? "Expand navigation" : "Minimize navigation"}
            className="p-6 rounded hover:bg-raised focus-visible:outline-none focus-visible:shadow-focus"
          >
            {collapsed ? (
              <PanelLeftOpen size={16} />
            ) : (
              <PanelLeftClose size={16} />
            )}
          </button>
        </div>
        <SidebarNav collapsed={collapsed} />
        <SidebarFooter
          email={data.patient.email}
          backToAdmin={backToAdmin}
          onBackToAdmin={() => navigate("/admin")}
          collapsed={collapsed}
        />
      </aside>

      <main
        className={`transition-[padding] duration-200 ${collapsed ? "md:pl-[64px]" : "md:pl-[230px]"}`}
      >
        <Outlet />
      </main>
    </div>
  );
}

function Brand() {
  return (
    <div className="flex items-center gap-10">
      <span className="type-label-md font-semibold">System Clinic</span>
      <span className="text-10 uppercase tracking-wider text-ink-muted border border-line px-6 py-2 rounded">
        clinical
      </span>
    </div>
  );
}

function SidebarNav({
  onNavigate,
  collapsed = false,
}: {
  onNavigate?: () => void;
  collapsed?: boolean;
}) {
  const { data } = useBootstrap();
  const { data: clinicianSession, isLoading: sessionLoading } =
    useClinicianSession();
  const navigate = useNavigate();
  const location = useLocation();
  const templatesActive = location.pathname.startsWith(
    "/clinician-portal/templates",
  );
  // Prescribers and nurses keep their own visit hours; supervisors and admins
  // can manage the team's. Hidden while the editor is switched off.
  const supervisor =
    data?.admin_role === "admin" || data?.admin_role === "clinical_supervisor";
  const availabilityEnabled = useAvailabilityEditorEnabled(
    supervisor ||
      clinicianSession?.provider_type === "prescriber" ||
      clinicianSession?.provider_type === "nurse",
    clinicianSession?.clinician_availability_editor_enabled,
    !sessionLoading,
  );
  const [templatesOpen, setTemplatesOpen] = useState(templatesActive);

  useEffect(() => {
    if (templatesActive) setTemplatesOpen(true);
  }, [templatesActive]);

  return (
    <nav className="flex-1 overflow-y-auto px-10 py-16 flex flex-col gap-20">
      {NAV_GROUPS.map((group) => (
        <div key={group.label} className="flex flex-col gap-4">
          {!collapsed && (
            <span className="px-10 text-10 uppercase tracking-wider text-ink-muted">
              {group.label}
            </span>
          )}
          {group.items.map((item) => (
            <Fragment key={item.to}>
              <ClinicianNavLink
                to={item.to}
                onClick={onNavigate}
                icon={item.icon}
                collapsed={collapsed}
              >
                {item.title}
              </ClinicianNavLink>
              {group.label === "Work" &&
                item.title === "Clinical worklist" &&
                (clinicianSession?.provider_type === "prescriber" ||
                  data?.admin_role === "admin" ||
                  data?.admin_role === "clinical_supervisor") && (
                  <ClinicianNavLink
                    to="/clinician-portal/cosign"
                    onClick={onNavigate}
                    icon={FileSignature}
                    collapsed={collapsed}
                  >
                    Chart reviews
                  </ClinicianNavLink>
                )}
              {group.label === "Work" &&
                item.title === "Inbox" &&
                clinicianSession?.provider_type === "nurse" && (
                  <ClinicianNavLink
                    to="/clinician-portal/nurse-appointments"
                    onClick={onNavigate}
                    icon={CalendarClock}
                    collapsed={collapsed}
                  >
                    Nurse appointments
                  </ClinicianNavLink>
                )}
            </Fragment>
          ))}
        </div>
      ))}
      <div className="flex flex-col gap-4">
        {!collapsed && (
          <span className="px-10 text-10 uppercase tracking-wider text-ink-muted">
            Tools
          </span>
        )}
        {availabilityEnabled && (
          <ClinicianNavLink
            to="/clinician-portal/availability"
            onClick={onNavigate}
            icon={CalendarDays}
            collapsed={collapsed}
          >
            Availability
          </ClinicianNavLink>
        )}
        <button
          type="button"
          onClick={() => {
            if (collapsed) {
              navigate("/clinician-portal/templates/scripts");
              return;
            }
            setTemplatesOpen((value) => !value);
          }}
          title={collapsed ? "Templates" : undefined}
          aria-expanded={collapsed ? undefined : templatesOpen}
          className={`py-8 rounded type-label-sm flex items-center ${collapsed ? "px-0 justify-center" : "px-10 gap-8"} ${templatesActive ? "bg-raised text-ink font-medium" : "text-ink-muted hover:text-ink hover:bg-raised/60"}`}
        >
          <BookOpenText size={15} className="shrink-0" aria-hidden />
          {!collapsed && (
            <>
              <span>Templates</span>
              <ChevronDown
                size={13}
                className={`ml-auto transition-transform ${templatesOpen ? "rotate-180" : ""}`}
                aria-hidden
              />
            </>
          )}
        </button>
        {!collapsed && templatesOpen && (
          <div className="ml-15 pl-8 border-l border-line-subtle flex flex-col gap-2">
            <TemplateSubLink
              to="/clinician-portal/templates/scripts"
              icon={BookOpenText}
              onClick={onNavigate}
            >
              Scripts
            </TemplateSubLink>
            <TemplateSubLink
              to="/clinician-portal/templates/intakes"
              icon={ClipboardList}
              onClick={onNavigate}
            >
              Intake forms
            </TemplateSubLink>
            <TemplateSubLink
              to="/clinician-portal/templates/guides"
              icon={FileText}
              onClick={onNavigate}
            >
              Guides
            </TemplateSubLink>
          </div>
        )}
        {(data?.admin_role === "admin" ||
          data?.admin_role === "clinical_supervisor") && (
          <ClinicianNavLink
            to="/clinician-portal/team"
            onClick={onNavigate}
            icon={UsersRound}
            collapsed={collapsed}
          >
            Clinical team
          </ClinicianNavLink>
        )}
        <ClinicianNavLink
          to="/clinician-portal/rules"
          onClick={onNavigate}
          icon={SlidersHorizontal}
          collapsed={collapsed}
        >
          Rules &amp; automations
        </ClinicianNavLink>
      </div>
    </nav>
  );
}

function TemplateSubLink({
  to,
  icon: Icon,
  children,
  onClick,
}: {
  to: string;
  icon: LucideIcon;
  children: React.ReactNode;
  onClick?: () => void;
}) {
  return (
    <NavLink
      to={to}
      onClick={onClick}
      className={({ isActive }) =>
        `px-8 py-7 rounded type-label-xs flex items-center gap-7 ${
          isActive
            ? "bg-raised text-ink font-medium"
            : "text-ink-muted hover:text-ink hover:bg-raised/60"
        }`
      }
    >
      <Icon size={13} aria-hidden /> {children}
    </NavLink>
  );
}

function SidebarFooter({
  email,
  backToAdmin,
  onBackToAdmin,
  collapsed = false,
}: {
  email: string;
  backToAdmin: boolean;
  onBackToAdmin: () => void;
  collapsed?: boolean;
}) {
  if (collapsed) return null;
  return (
    <div className="border-t border-line px-15 py-15 flex flex-col gap-10">
      <span className="type-label-sm text-ink-muted truncate" title={email}>
        {email}
      </span>
      {backToAdmin && (
        <button
          type="button"
          onClick={onBackToAdmin}
          className="type-label-sm px-10 py-6 rounded border border-line hover:bg-raised text-left"
        >
          Admin portal <ArrowInline />
        </button>
      )}
    </div>
  );
}

function ClinicianNavLink({
  to,
  children,
  onClick,
  icon: Icon,
  collapsed = false,
}: {
  to: string;
  children: React.ReactNode;
  onClick?: () => void;
  icon: LucideIcon;
  collapsed?: boolean;
}) {
  return (
    <NavLink
      to={to}
      end={to === "/clinician-portal"}
      onClick={onClick}
      title={collapsed ? String(children) : undefined}
      aria-label={collapsed ? String(children) : undefined}
      className={({ isActive }) =>
        `py-8 rounded type-label-sm flex items-center ${collapsed ? "px-0 justify-center" : "px-10 gap-8"} ${
          isActive
            ? "bg-raised text-ink font-medium"
            : "text-ink-muted hover:text-ink hover:bg-raised/60"
        }`
      }
    >
      <Icon size={15} className="shrink-0" aria-hidden />
      {!collapsed && children}
    </NavLink>
  );
}
